import { useQuery } from "@tanstack/react-query";
import { Image } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { feedbackKeys, myScreenshotLink } from "./queries.ts";

/** The PNG behind a 60-second URL, as a local `blob:` URL (the page's policy loads no bucket image). */
async function screenshotBlob(reportId: string): Promise<Blob> {
  const link = await myScreenshotLink(reportId);
  const res = await fetch(link.url, { credentials: "omit" });
  if (!res.ok) {
    throw new Error("screenshot_unavailable");
  }
  return res.blob();
}

/**
 * The reporter's own screenshot in "My feedback", loaded on demand through a minted URL
 * (openspec change `in-app-feedback`, design D4).
 */
export function MyScreenshot({ reportId }: { reportId: string }) {
  const { t } = useTranslation();
  const [shown, setShown] = useState(false);
  const blob = useQuery({
    queryKey: feedbackKeys.screenshot(reportId),
    queryFn: () => screenshotBlob(reportId),
    enabled: shown,
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!blob.data) {
      return;
    }
    const next = URL.createObjectURL(blob.data);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [blob.data]);
  return (
    <div className="flex min-w-0 flex-col gap-2">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="self-start"
        aria-expanded={shown}
        onClick={() => setShown(!shown)}
      >
        <Image aria-hidden="true" strokeWidth={1.75} />
        {shown ? t("feedbackScreenshot.hide") : t("feedbackScreenshot.view")}
      </Button>
      {shown ? (
        blob.isError ? (
          <p className="text-xs text-fg-muted">{t("feedbackScreenshot.unavailable")}</p>
        ) : url ? (
          <img
            src={url}
            alt={t("feedbackScreenshot.label")}
            className="max-h-72 w-full rounded-md border border-line bg-surface-sunken object-contain"
          />
        ) : (
          <p className="text-xs text-fg-muted">{t("feedbackScreenshot.loading")}</p>
        )
      ) : null}
    </div>
  );
}
